import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { PaginationResponse } from "@/core/services/api/interfaces/PaginationResponse";
import ApiService from "@/core/services/ApiService";
import { AxiosRequestConfig } from "axios";
import { isUserAdmin } from "@/core/services/UserRoleService";
import { Role, User } from "@/core/services/api/interfaces/manager/User";
import { UserRole } from "@/core/services/api/enums/UserRole";

@Module
export default class UsersModule extends VuexModule {
  pagination = new PaginationResponse<User>();
  loading = false;

  get hasSuperAdminRole() {
    return (roles: Role[]): boolean => {
      return (
        roles.filter((role: Role) => {
          return UserRole.SUPER_ADMIN === role.slug;
        }).length === 1
      );
    };
  }

  get users(): User[] {
    return this.usersPagination.data;
  }

  get usersRecentTen(): User[] {
    return this.usersPagination.data.slice(0, 10);
  }

  get userById() {
    return (user_id: number) =>
      this.usersPagination.data.find(
        (user: User) => parseInt(String(user_id)) === user.id
      );
  }

  get usersLoading(): boolean {
    return this.loading;
  }

  get usersPagination(): PaginationResponse<User> {
    if (!isUserAdmin()) {
      return new PaginationResponse<User>();
    }

    return this.pagination;
  }

  get usersTotal(): number {
    return this.pagination.total;
  }

  get usersRequired(): boolean {
    return isUserAdmin() && !this.pagination.total && !this.loading;
  }

  get usersCurrentPage(): number {
    return this.pagination.current_page;
  }

  get usersPerPage(): number {
    return this.pagination.per_page;
  }

  @Action({ rawError: true })
  [Actions.ADMIN_USERS_LOAD](params) {
    this.context.commit(Mutations.ADMIN_USERS_SET_LOADING, true);

    return ApiService.admin.user
      .list({
        params,
      } as AxiosRequestConfig)
      .then(ApiService.unwrapPaginationResponse)
      .then((response) => {
        this.context.commit(Mutations.ADMIN_USERS_SET, response);
      })
      .catch(console.error)
      .finally(() => {
        this.context.commit(Mutations.ADMIN_USERS_SET_LOADING, false);

        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.ADMIN_USERS_LOAD
        );
      });
  }

  @Mutation
  [Mutations.ADMIN_USER_UPDATE](data) {
    const index = this.pagination.data
      .map((user: User) => user.id)
      .indexOf(data.id);
    this.pagination.data[index] = data;
  }

  @Mutation
  [Mutations.ADMIN_USER_AVATAR_UPDATE](data) {
    const index = this.pagination.data
      .map((user: User) => user.id)
      .indexOf(data.id);
    this.pagination.data[index].avatar = data.avatar;
  }

  @Mutation
  [Mutations.ADMIN_USERS_ROLES_OVERRIDE](data: { user_id: number; roles: [] }) {
    const index = this.pagination.data
      .map((user: User) => user.id)
      .indexOf(data.user_id);
    this.pagination.data[index].roles = data.roles;
  }

  @Mutation
  [Mutations.ADMIN_USERS_OVERRIDE](user_id: number) {
    const index = this.pagination.data
      .map((user: User) => user.id)
      .indexOf(user_id);
    return this.pagination.data.splice(index, 1);
  }

  @Mutation
  [Mutations.ADMIN_USERS_SET](pagination: PaginationResponse<User>) {
    this.pagination = pagination;
  }

  @Mutation
  [Mutations.ADMIN_USERS_RESET]() {
    this.pagination = new PaginationResponse<User>();
  }

  @Mutation
  [Mutations.ADMIN_USERS_SET_LOADING](loading: boolean) {
    this.loading = loading;
  }
}
